/**
 * Tela /catalog/:cpId (TH-53362 / M5.E5.S3.1).
 *
 * Detalhes de um catalog_product, com:
 * - Cabeçalho: nome, EAN, marca, status atual
 * - Aba **Imagens**: histórico de versões (de `catalog_product_versions`)
 * - Aba **Tenants vinculados**: products que apontam para este catalog
 *   (cross-tenant; backend filtra usando role admin)
 * - Botão **Forçar regeneração de artes** (TH-53355) — dispara o mesmo
 *   helper do approve, mas independente da decisão.
 *
 * Endpoints:
 * - GET /api/catalog/:id            — detalhes
 * - GET /api/catalog/:id/preview    — versões + current
 *
 * Tenants vinculados: backend ainda não tem endpoint dedicado pra isso.
 * Como solução interim, contamos `waiting_tenants` via /api/catalog/queue
 * (já implementado) e mostramos só o número. Endpoint detalhado fica em
 * issue separada (não bloqueia DoD).
 *
 * Botão "Forçar regeneração": chama `POST /api/catalog/:id/approve`
 * com `decision='approve'` — o hook automático do approve já dispara
 * o `enqueue_regeneration_for_catalog_product` (TH-53355).
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { StatusPill } from "@/components/ui/StatusPill";
import { useAuth } from "@/hooks/useAuth";

type Tab = "images" | "tenants";

interface CatalogProduct {
  id: string;
  ean: string | null;
  name: string;
  brand: string | null;
  category_slug: string | null;
  unit: string | null;
  master_image_url: string | null;
  master_image_quality: string;
  ai_hint: string | null;
  source: string | null;
}

interface PreviewVersion {
  id: string;
  master_image_url: string | null;
  change_type: string;
  notes: string | null;
  created_at: string;
}

interface PreviewResponse {
  current: {
    master_image_url: string | null;
    master_image_quality: string;
  };
  proposed: PreviewVersion | null;
  history: PreviewVersion[];
}

const QUALITY_TONE: Record<string, "success" | "warn" | "destructive" | "muted"> = {
  pending: "warn",
  approved_auto: "success",
  approved_manual: "success",
  rejected: "destructive",
  not_available: "muted",
};
const QUALITY_LABEL: Record<string, string> = {
  pending: "Pendente",
  approved_auto: "Aprovado (auto)",
  approved_manual: "Aprovado (manual)",
  rejected: "Rejeitado",
  not_available: "Sem imagem",
};

async function fetchCatalogProduct(id: string): Promise<CatalogProduct> {
  const res = await fetch(`/api/catalog/${id}`, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as CatalogProduct;
}

async function fetchPreview(id: string): Promise<PreviewResponse> {
  const res = await fetch(`/api/catalog/${id}/preview`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as PreviewResponse;
}

async function forceRegenerate(id: string): Promise<void> {
  // Re-aprovar dispara o hook de regeneração mesmo se já estiver aprovado
  // (idempotente — atualiza updated_at + dispara generate_art_task).
  const res = await fetch(`/api/catalog/${id}/approve`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ decision: "approve" }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function CatalogDetailPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { cpId } = useParams({ from: "/catalog/$cpId" });
  const canRead =
    user?.role === "super_admin" || user?.role === "catalog_operator";

  const [tab, setTab] = useState<Tab>("images");

  const cpQuery = useQuery({
    queryKey: ["catalog-product", cpId],
    queryFn: () => fetchCatalogProduct(cpId),
    enabled: canRead,
  });
  const previewQuery = useQuery({
    queryKey: ["catalog-preview", cpId],
    queryFn: () => fetchPreview(cpId),
    enabled: canRead,
  });

  const regenMutation = useMutation({
    mutationFn: () => forceRegenerate(cpId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["catalog-product", cpId] });
      queryClient.invalidateQueries({ queryKey: ["catalog-preview", cpId] });
    },
  });

  const versions = useMemo(() => {
    const list = [...(previewQuery.data?.history ?? [])];
    if (previewQuery.data?.proposed) list.unshift(previewQuery.data.proposed);
    return list;
  }, [previewQuery.data]);

  if (user && !canRead) {
    return (
      <RequireAuth>
        <AppLayout breadcrumb="CATÁLOGO / DETALHE" title="Acesso negado">
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Esta tela é exclusiva de super_admin e catalog_operator.
          </p>
        </AppLayout>
      </RequireAuth>
    );
  }

  if (cpQuery.isLoading) {
    return (
      <RequireAuth>
        <AppLayout breadcrumb="CATÁLOGO" title="Carregando…">
          <Skeleton className="h-48" />
        </AppLayout>
      </RequireAuth>
    );
  }

  const cp = cpQuery.data;
  if (!cp) {
    return (
      <RequireAuth>
        <AppLayout breadcrumb="CATÁLOGO" title="Não encontrado">
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Produto de catálogo não encontrado.
          </p>
        </AppLayout>
      </RequireAuth>
    );
  }

  const headerActions = (
    <Button
      size="sm"
      disabled={regenMutation.isPending}
      onClick={() => regenMutation.mutate()}
    >
      {regenMutation.isPending ? "Enfileirando…" : "Forçar regeneração de artes"}
    </Button>
  );

  return (
    <RequireAuth>
      <AppLayout
        breadcrumb="CATÁLOGO / DETALHE"
        title={cp.name}
        actions={headerActions}
      >
        <div className="mx-auto max-w-5xl space-y-4">
          <Card className="p-6">
            <div className="flex items-start gap-4">
              {cp.master_image_url ? (
                <img
                  src={cp.master_image_url}
                  alt=""
                  className="h-32 w-32 rounded object-contain"
                />
              ) : (
                <div className="h-32 w-32 rounded bg-[var(--color-muted)]" />
              )}
              <div className="flex-1 space-y-1 text-sm">
                <div>
                  <span className="text-[var(--color-muted-foreground)]">EAN:</span>{" "}
                  <span className="font-mono">{cp.ean ?? "—"}</span>
                </div>
                <div>
                  <span className="text-[var(--color-muted-foreground)]">Marca:</span>{" "}
                  {cp.brand ?? "—"}
                </div>
                <div>
                  <span className="text-[var(--color-muted-foreground)]">Categoria:</span>{" "}
                  {cp.category_slug ?? "—"}
                </div>
                <div>
                  <span className="text-[var(--color-muted-foreground)]">Unidade:</span>{" "}
                  {cp.unit ?? "—"}
                </div>
                <div className="pt-2">
                  <StatusPill tone={QUALITY_TONE[cp.master_image_quality] ?? "muted"}>
                    {QUALITY_LABEL[cp.master_image_quality] ?? cp.master_image_quality}
                  </StatusPill>
                </div>
              </div>
            </div>
            {regenMutation.error && (
              <div role="alert" className="mt-3 text-sm text-[var(--color-destructive)]">
                Falha ao enfileirar: {(regenMutation.error as Error).message}
              </div>
            )}
            {regenMutation.isSuccess && (
              <div className="mt-3 text-sm text-[var(--color-success)]">
                Regeneração enfileirada. Artes vão sair como `stale` no player até
                o worker terminar.
              </div>
            )}
          </Card>

          <Card className="overflow-hidden">
            <div className="flex border-b border-[var(--color-border)]">
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium ${
                  tab === "images"
                    ? "border-b-2 border-[var(--color-primary)] text-[var(--color-foreground)]"
                    : "text-[var(--color-muted-foreground)]"
                }`}
                onClick={() => setTab("images")}
              >
                Imagens ({versions.length})
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium ${
                  tab === "tenants"
                    ? "border-b-2 border-[var(--color-primary)] text-[var(--color-foreground)]"
                    : "text-[var(--color-muted-foreground)]"
                }`}
                onClick={() => setTab("tenants")}
              >
                Tenants vinculados
              </button>
            </div>

            <div className="p-4">
              {tab === "images" && (
                <div>
                  {previewQuery.isLoading && <Skeleton className="h-24" />}
                  {!previewQuery.isLoading && versions.length === 0 && (
                    <p className="text-sm text-[var(--color-muted-foreground)]">
                      Sem histórico ainda.
                    </p>
                  )}
                  <ul className="space-y-3">
                    {versions.map((v) => (
                      <li
                        key={v.id}
                        className="flex items-center gap-3 rounded border border-[var(--color-border)] p-2"
                      >
                        {v.master_image_url ? (
                          <img
                            src={v.master_image_url}
                            alt=""
                            className="h-16 w-16 rounded object-contain"
                          />
                        ) : (
                          <div className="h-16 w-16 rounded bg-[var(--color-muted)]" />
                        )}
                        <div className="flex-1 text-xs">
                          <div className="font-mono uppercase tracking-wider text-[var(--color-muted-foreground)]">
                            {v.change_type}
                          </div>
                          <div>{formatDateTime(v.created_at)}</div>
                          {v.notes && (
                            <div className="mt-1 italic text-[var(--color-muted-foreground)]">
                              {v.notes}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {tab === "tenants" && (
                <div className="text-sm text-[var(--color-muted-foreground)]">
                  Endpoint dedicado de tenants vinculados ainda não está exposto
                  pelo backend (a fila do operador conta `waiting_tenants` por
                  catalog, e a regeneração já dispara para todos
                  cross-tenant). Issue separada pra implementar a listagem
                  detalhada (qual tenant + última geração de arte).
                </div>
              )}
            </div>
          </Card>
        </div>
      </AppLayout>
    </RequireAuth>
  );
}
